import { useContext, useEffect, useState } from 'react'
import api from '../api'
import MainContext from '../common/MainContext'
import { AdminRole } from '../common/constants'
import Button from './Button'
import Card from './cards/Card'
import Pagination from './Pagination'
import Select from './Select'
import styles from './EventTalentCvs.module.css'

const states = {
  sent: ['Trasmesso', 'sent'], pending: ['In attesa', 'pending'], sending: ['In invio', 'pending'],
  failed: ['Invio fallito', 'attention'], uncertain: ['Esito da verificare', 'attention'],
  cancelled: ['Annullato', 'neutral'], not_queued: ['Non in coda', 'neutral'],
}
const options = [{ id: 'all', name: 'Tutti gli stati', value: '' }, ...Object.entries(states).map(([key, [name]]) => ({ id: key, name, value: key }))]
const metrics = [['total', 'CV caricati'], ['sent', 'Trasmessi a iziwork'], ['pending', 'In attesa / in invio'], ['attention', 'Da verificare'], ['not_queued', 'Non in coda'], ['cancelled', 'Annullati']]
const dateLabel = value => value ? new Date(value).toLocaleString('it-IT') : '—'

export default function EventTalentCvs({ eventId }) {
  const { user } = useContext(MainContext)
  const allowed = user?.role === AdminRole.Main && !user?.company && !user?.company_id
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [page, setPage] = useState(0)
  const [status, setStatus] = useState('')
  const [search, setSearch] = useState('')
  const [query, setQuery] = useState('')
  const [refresh, setRefresh] = useState(0)
  useEffect(() => {
    const timer = setTimeout(() => { setQuery(search.trim()); setPage(0) }, 300)
    return () => clearTimeout(timer)
  }, [search])
  useEffect(() => { setPage(0); setData(null) }, [eventId])
  useEffect(() => {
    if (!allowed || !eventId) return
    let active = true
    setLoading(true)
    setError('')
    const params = new URLSearchParams({ page, per: 25, ...(status ? { status } : {}), ...(query ? { q: query } : {}) })
    api.get(`/admin/events/${eventId}/talent-cvs?${params}`).then(result => {
      if (active) setData(result)
    }).catch(() => {
      if (active) { setData(null); setError('Non è stato possibile caricare i CV. Premi Aggiorna per riprovare.') }
    }).finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [allowed, eventId, page, status, query, refresh])
  if (!allowed) return null
  return <Card appearance="modern" title="CV · IZIWORK" actions={<Button appearance="subtle" requiresEdit={false} disabled={loading} onClick={() => setRefresh(value => value + 1)}>Aggiorna</Button>}>
    <div className={styles.content} aria-busy={loading}>
      <p className={styles.intro}>CV attualmente associati ai biglietti e agli stand di questo evento. Un CV condiviso tramite più biglietti o stand compare per ogni associazione.</p>
      <div className={styles.metrics}>
        {metrics.map(([key, label]) => <div className={styles.metric} key={key}><span>{label}</span><strong>{data ? data.summary[key].toLocaleString('it-IT') : '—'}</strong></div>)}
      </div>
      <div className={styles.filters}>
        <label className={styles.search}>Cerca candidato o file<input type="search" placeholder="Nome, email o nome del file" value={search} onChange={e => setSearch(e.target.value)} /></label>
        <div className={styles.statusFilter}><span>Stato di trasmissione</span><Select appearance="modern" portal options={options} selected={options.find(option => option.value === status)} onSelect={option => { setStatus(option.value); setPage(0) }} /></div>
      </div>
      {error && <p role="alert" className={styles.error}>{error}</p>}
      {loading ? <p role="status" className={styles.empty}>Caricamento CV…</p> : data && (data.items.length ? <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <caption className={styles.srOnly}>CV dell’evento e stato di trasmissione a iziwork</caption>
          <thead><tr><th scope="col">Candidato</th><th scope="col">CV e provenienza</th><th scope="col">Stato</th><th scope="col">Trasmesso il</th></tr></thead>
          <tbody>{data.items.map(item => {
            const [label, tone] = states[item.status] || ['Sconosciuto', 'neutral']
            return <tr key={item.id}>
              <td><strong>{item.candidate || 'Nome non disponibile'}</strong><span className={styles.secondary}>{item.email || '—'}</span></td>
              <td><span>{item.file_name}</span><span className={styles.secondary}>{item.source === 'stand' ? `Stand · ${item.origin}` : `Biglietto #${item.source_id}`}</span></td>
              <td><span className={`${styles.badge} ${styles[tone]}`}>{label}</span>{item.attempts > 0 && <span className={styles.secondary}>Tentativi: {item.attempts}</span>}</td>
              <td>{dateLabel(item.sent_at)}</td>
            </tr>
          })}</tbody>
        </table>
      </div> : <p className={styles.empty}>{data.summary.total ? 'Nessun CV corrisponde ai filtri selezionati.' : 'Nessun CV caricato per questo evento.'}</p>)}
      {data?.count > 0 && <Pagination page={page} total={data.count} per={25} loading={loading} onChange={setPage} />}
      <p className={styles.note}>“Trasmesso” indica che il server email ha accettato l’invio a iziwork. “Esito da verificare” richiede un controllo prima di riprovare. I conteggi si riferiscono all’intero evento.</p>
    </div>
  </Card>
}
